import PropTypes from 'prop-types';
import { twJoin, twMerge } from 'tailwind-merge';

import Heading from 'shopper/components/Heading';
import Icon from 'shopper/components/Icon';
import Price from 'shopper/components/Price/V2';
import Text from 'shopper/components/Text';

import Anchor from 'components/Anchor';

import { sendEvent } from 'lib/gtag';
import { toFormattedOfferOldPrice } from 'lib/offer';
import { toFormattedOfferPrice } from 'lib/offerPage';
import { scrollToElement } from 'lib/utils';

const ALERT_TYPES = {
  PROMOBIT_HAS_THE_BEST_PRICE: {
    icon: {
      name: 'search-right',
      color: 'text-success-200',
      bg: 'bg-success-100',
    },
    colors: {
      bg: 'bg-success-100/20',
      border: 'border-success-200',
      hover: 'hover:bg-success-100/30',
    },
    title: 'Este é o menor preço!',
    description:
      'Você encontrou a melhor oferta disponível entre 5 lojas que possuem este produto.',
  },
  COMPARATOR_HAS_THE_BEST_PRICE: {
    icon: {
      name: 'search-right',
      color: 'text-primary-300',
      bg: 'bg-primary-100',
    },
    colors: {
      bg: 'bg-primary-200/20',
      border: 'border-primary-200',
      hover: 'hover:bg-primary-200/30',
    },
    title: 'Compare e economize',
    description:
      'Confira no comparador de preços todas as lojas e descubra o menor valor.',
  },
};

const PriceComparatorAlert = ({
  className,
  gaEventCategory,
  isPromobitHasTheBestPrice = false,
  offerOldPrice = null,
  offerPrice,
  offerPriceType,
}) => {
  const alertMetadata = isPromobitHasTheBestPrice
    ? ALERT_TYPES.PROMOBIT_HAS_THE_BEST_PRICE
    : ALERT_TYPES.COMPARATOR_HAS_THE_BEST_PRICE;

  const onHandleClick = () => {
    const priceComparatorSection = document.getElementById('price-comparator');

    if (priceComparatorSection) {
      scrollToElement(priceComparatorSection.offsetTop);
    }

    sendEvent({
      action: 'click_compare_anchor',
      category: gaEventCategory,
      label: isPromobitHasTheBestPrice
        ? 'compare_best_price'
        : 'compare_not_best_price',
    });
  };

  return (
    <Anchor
      className={twMerge(
        'flex w-full cursor-pointer flex-row rounded-3 border px-2 py-2 md:px-4',
        alertMetadata.colors.bg,
        alertMetadata.colors.border,
        alertMetadata.colors.hover,
        className
      )}
      onClick={onHandleClick}
    >
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={twJoin(
              'flex size-8 min-w-8 items-center justify-center rounded-full',
              alertMetadata.icon.bg
            )}
          >
            <Icon
              className={alertMetadata.icon.color}
              name={alertMetadata.icon.name}
              size="size1"
            />
          </span>
          <div>
            <Heading size="size6">{alertMetadata.title}</Heading>
            {isPromobitHasTheBestPrice && (
              <Price
                className="mt-1 lg:mt-0 lg:items-center"
                mobileOrientation="row"
                primaryInfo={toFormattedOfferPrice(offerPrice, offerPriceType)}
                secondaryInfo={toFormattedOfferOldPrice(
                  offerOldPrice,
                  offerPriceType
                )}
                size="size2"
              />
            )}
            <Text className="text-sm md:text-base">
              {alertMetadata.description}
            </Text>
          </div>
        </div>
        <Icon
          className={alertMetadata.icon.color}
          name="arrow-forward"
          size="size1"
        />
      </div>
    </Anchor>
  );
};

PriceComparatorAlert.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  isPromobitHasTheBestPrice: PropTypes.bool,
  offerOldPrice: PropTypes.number,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
};

export default PriceComparatorAlert;
